import { useEffect, useMemo } from "react";
import { captureException } from "@sentry/nextjs";
import { useExperiment } from "@statsig/react-bindings";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "next/navigation";

import { analytics } from "@flare/analytics";
import { useSession } from "@flare/next-auth/react";
import { Text } from "@flare/ui/components";

import { type IntakeMeetingSlots } from "@/edge/contract";
import { jsonRequest } from "@/lib/network/json-request";
import type { Practices } from "@/modules/v2/funnel/config";
import { funnelInputsNameMap } from "@/modules/v2/funnel/config";
import { saveToFunnelMeta } from "@/modules/v2/funnel/funnel.utils";
import { useFunnelAnswers } from "@/modules/v2/funnel/hooks/useFunnelAnswers";
import { getCmnEligibility } from "@/modules/v2/funnel/visitor-checks/getCmnEligibility";
import { BiEventsNames } from "@/services/analytics/bi-event-names";
import { STATSIG_EXPERIMENT } from "@/services/experiments/constants";

import { ChoiceBoxItem } from "../../../components/Funnel/ChoiceBox";
import { meetingType } from "../funnel.consts";
import { Scheduler } from "../Scheduler/Scheduler";
import { getTimeZoneAbbreviation } from "../Scheduler/Scheduler.utils";

import {
  getTimeSlots,
  groupByDate,
  shouldShowSlot,
} from "./IntakeScheduler.utils";

export const PracticeEnum = {
  family: "family",
  immigration: "immigration",
} as const;

export const NUMBER_OF_DAYS = 7;
const [today] = new Date().toISOString().split("T");

const clientTimeZone = getTimeZoneAbbreviation() || "";

export function IntakeScheduler() {
  const params = useParams<{ practice: keyof Practices; step: string }>();

  const session = useSession();
  const { funnelAnswers, setFunnelAnswers } = useFunnelAnswers();

  const practice = params.practice || session.data?.user.funnelData?.practice;

  const searchParams = new URLSearchParams({
    practice,
    numberOfDays: NUMBER_OF_DAYS.toString(),
    rangeStart: today,
  }).toString();

  const {
    data: groupedIntakeSlots = {},
    isLoading,
    error,
  } = useQuery({
    queryKey: ["intakeMeetingSlots", searchParams],
    queryFn: async () => {
      const endpoint = `/api/vinny/calendars/intake-slots?${searchParams}`;
      const data = await jsonRequest<IntakeMeetingSlots>(endpoint, {
        method: "GET",
      });

      if (data) {
        return groupByDate(data.slots);
      }
    },
  });

  const immigrationCMNExperiment = useExperiment(
    STATSIG_EXPERIMENT.IMMIGRATION_CMN,
  );

  const isImmigrationCMNExperimentEnabled =
    immigrationCMNExperiment.get("groupSymbol", "disabled") === "enabled";

  const cmnEligibility = useMemo(() => {
    return getCmnEligibility(
      {
        practice,
        leadIntent: funnelAnswers.leadIntent,
      },
      isImmigrationCMNExperimentEnabled,
    );
  }, [
    funnelAnswers.leadIntent,
    funnelAnswers.practice,
    isImmigrationCMNExperimentEnabled,
  ]);

  useEffect(() => {
    setFunnelAnswers((prev) => {
      return {
        ...prev,
        isEligibleForCmn: cmnEligibility.isEligible,
      };
    });
  }, [cmnEligibility.isEligible, setFunnelAnswers]);

  useEffect(() => {
    if (
      funnelAnswers.timezone !== clientTimeZone ||
      funnelAnswers.meetingType !== meetingType.intake
    ) {
      setFunnelAnswers((prev) => {
        return {
          ...prev,
          timezone: clientTimeZone,
          meetingType: meetingType.intake,
        };
      });
    }
  }, [funnelAnswers.timezone, funnelAnswers.meetingType, setFunnelAnswers]);

  useEffect(() => {
    analytics.track(BiEventsNames.WebCmnEligibility, {
      lead_intent: funnelAnswers.leadIntent,
      lead_score: funnelAnswers.leadScore,
      is_eligible: cmnEligibility.isEligible,
      in_business_hours: cmnEligibility.isAvailable,
      business_hour_open: cmnEligibility.availabilityRange?.open ?? "closed",
      business_hour_close: cmnEligibility.availabilityRange?.close ?? "closed",
    });
  }, [cmnEligibility, funnelAnswers.leadIntent, funnelAnswers.leadScore]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const hasSlotsToday = Boolean(groupedIntakeSlots[today]);
    const [firstAvailableDate] = Object.keys(groupedIntakeSlots);

    const timeSlots = getTimeSlots({
      slots: groupedIntakeSlots[firstAvailableDate],
      isEligibleForCmn: cmnEligibility.isEligible,
    });

    const firstPresentedTimeSlot = timeSlots?.[0];

    analytics.track(BiEventsNames.WebCalendarPageView, {
      range_start: today,
      number_of_fetched_days: Object.keys(groupedIntakeSlots).length,
      practice,
      timezone: clientTimeZone,
      first_presented_time_slot: firstPresentedTimeSlot,
      number_of_time_slots: timeSlots?.length,
      current_step_key: params?.step,
      meeting_type: meetingType.intake,
      call_me_now_visible: Boolean(cmnEligibility.isEligible && hasSlotsToday),
    });
  }, [isLoading, groupedIntakeSlots, practice, cmnEligibility, params?.step]);

  useEffect(() => {
    if (error) {
      analytics.track(BiEventsNames.WebFunnelError, {
        error_type: "get_intake_meeting_slots_error",
        error_message: error.message,
        practice,
        flare_id: session?.data?.user?.id || null,
      });
      captureException(error, {
        extra: {
          action: "intakeMeetingSlots",
        },
      });
    }
  }, [error, funnelAnswers.practice, practice, session?.data?.user?.id]);

  function onDayChange(selectedDay: string) {
    if (!groupedIntakeSlots) return;
    const selectedDayIndex =
      Object.keys(groupedIntakeSlots).indexOf(selectedDay);

    const timeSlots = getTimeSlots({
      slots: groupedIntakeSlots[selectedDay],
      isEligibleForCmn: cmnEligibility.isEligible,
    });

    const firstPresentedTimeSlotStartTime = timeSlots?.[0]?.start.toISOString();

    analytics.track(BiEventsNames.WebCalendarClick, {
      timezone: clientTimeZone,
      first_presented_time_slot: firstPresentedTimeSlotStartTime,
      number_of_time_slots: timeSlots?.length,
      date_selected: selectedDay,
      date_slot_index: selectedDayIndex,
      number_of_fetched_days: Object.keys(groupedIntakeSlots).length,
      meeting_type: meetingType.intake,
    });
  }

  const [firstAvailableDate] = Object.keys(groupedIntakeSlots || {});

  const slots = groupedIntakeSlots[firstAvailableDate] || [];

  const timeSlots =
    slots.length === 0
      ? []
      : getTimeSlots({
          slots,
          isEligibleForCmn: cmnEligibility.isEligible,
        });

  const firstPresentedTimeSlot = timeSlots?.[0];

  useEffect(() => {
    saveToFunnelMeta({
      firstPresentedTimeSlotStartTime:
        firstPresentedTimeSlot?.start?.toISOString(),
    });
  }, [firstPresentedTimeSlot]);

  return (
    <Scheduler
      onDayChange={onDayChange}
      getDefaultSelectedDay={(slots) => Object.keys(slots)[0]}
      loading={isLoading}
      slots={groupedIntakeSlots}
      name={funnelInputsNameMap.eventStartTime}
      shouldShowSlot={cmnEligibility.isEligible ? shouldShowSlot : undefined}
      renderSlotListTopElement={(selectedDay) => {
        if (!cmnEligibility.isEligible || selectedDay !== today) {
          return undefined;
        }

        return (
          <ChoiceBoxItem
            aria-label="Time slot"
            value="call_me_now"
            key="call_me_now"
            className="flex items-center justify-between py-sp-5"
          >
            <Text variant="body1">I&apos;m available now</Text>
            <Text
              textColor="success"
              className="rounded-full bg-success px-sp-2 py-sp-1"
            >
              Best choice
            </Text>
          </ChoiceBoxItem>
        );
      }}
    />
  );
}
